import { ToggleButton, ToggleButtonGroup } from '@mui/material';

import { EsgRecordStatus } from '../../../types/syntiro_honey';

/** The coarse review scope, layered over the finer status/mapping filters. */
export type ReviewScope = 'all' | 'needsAction' | 'approved';

/** The status/excludeApproved a scope maps to (the rest of the filters is untouched). */
export interface ScopeFilters {
  status: EsgRecordStatus | undefined;
  excludeApproved: boolean | undefined;
}

/**
 * Derives the active scope from the current filters, so the tab strip reflects
 * state set elsewhere. An explicit APPROVED status reads as "approved"; the
 * needs-action scope as "needs action"; anything else (incl. a specific non-
 * approved status) as "all" — the tabs are a coarse layer over the filter bar.
 * @param {Pick<ScopeFilters,'status'|'excludeApproved'>} filters The active filters.
 * @returns {ReviewScope}
 */
export function scopeFromFilters(filters: {
  status?: EsgRecordStatus;
  excludeApproved?: boolean;
}): ReviewScope {
  if (filters.status === 'APPROVED') {
    return 'approved';
  }
  if (filters.excludeApproved === true) {
    return 'needsAction';
  }
  return 'all';
}

/**
 * The status/excludeApproved a scope selects. "All" clears both (approved records
 * stay visible — Maxine); "Needs action" excludes settled records; "Approved"
 * pins the APPROVED status.
 * @param {ReviewScope} scope The chosen scope.
 * @returns {ScopeFilters}
 */
export function filtersForScope(scope: ReviewScope): ScopeFilters {
  if (scope === 'approved') {
    return { status: 'APPROVED', excludeApproved: undefined };
  }
  if (scope === 'needsAction') {
    return { status: undefined, excludeApproved: true };
  }
  return { status: undefined, excludeApproved: undefined };
}

/** Sentence case and unemphatic — a filter, not a destination. */
const SCOPE_BUTTON_SX = { textTransform: 'none', px: 1.75, py: 0.5 } as const;

interface ReviewScopeTabsProps {
  /** The active scope (derived from the current filters). */
  scope: ReviewScope;
  /** Called with the newly-chosen scope. */
  onScopeChange: (scope: ReviewScope) => void;
}

/**
 * The review-scope strip: All · Needs action · Approved. It defaults to All so
 * approved records stay visible (locked) rather than being hidden — the reviewer
 * can switch to Needs action to focus, or Approved to see only settled records.
 *
 * A SEGMENTED CONTROL, not a tab bar. It was MUI `Tabs`, which put an underline
 * indicator — the strongest "this is navigation" signal available — on a control
 * that merely filters a list, ten pixels below the page's actual navigation
 * (Upload / Review / Learned rules / Admin), which is styled as pill buttons. The
 * visual weight was inverted: the subordinate control looked more like navigation
 * than the navigation did. One tab bar per screen; everything nested under it
 * reads lighter.
 * @param {ReviewScopeTabsProps} props The active scope and its change handler.
 * @returns {JSX.Element}
 */
export default function ReviewScopeTabs({ scope, onScopeChange }: ReviewScopeTabsProps) {
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={scope}
      // Exclusive groups emit null when the active button is re-clicked; a scope is
      // always one of the three, so re-clicking the current one is a no-op.
      onChange={(_, next: ReviewScope | null) => {
        if (next !== null) {
          onScopeChange(next);
        }
      }}
      aria-label="Review scope"
      sx={{ alignSelf: 'flex-start' }}
    >
      <ToggleButton value="all" sx={SCOPE_BUTTON_SX}>
        All
      </ToggleButton>
      <ToggleButton value="needsAction" sx={SCOPE_BUTTON_SX}>
        Needs action
      </ToggleButton>
      <ToggleButton value="approved" sx={SCOPE_BUTTON_SX}>
        Approved
      </ToggleButton>
    </ToggleButtonGroup>
  );
}
